import './Main.css'

import React, { useEffect, useMemo, useState } from 'react'
import { useWizard } from '@/common/wizard/wizard'
import { useAppContext } from '@/context/app'
import { WizardData } from './Model'
import { StaticPaginatedTable, StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'
import { TimeDHM } from '@/components/Elements/time'
import { secondsToDHM } from '@/common/time'
import { fetchExcel, fetchPDF } from '@/common/fetch'
import { Order, OrderManager } from '@/components/order/Order'
import { TimeZoneNotice } from '@/components/timezone/TimeZoneNotice'

export interface UserRange {
  onFireRatio: number;
  totalActive: number;
  totalBuckets: number;
  totalOnFire: number;
  totalMth?: number;
  userId: string;
  username: string;
}

const tenMinInSec = 60 * 10
const UserRangeOrderCols = ['username', 'totalOnFire', 'totalActive', 'totalMth', 'onFireRatio']
const UserRangeDefaultOrder: Order = {
  key: 'totalOnFire',
  asc: false
}

export const UsersReportWizardResult = () => {
  const { services, session, translates } = useAppContext()
  const { wizardData, updateWizardData, nextStep } = useWizard<WizardData>()
  const [data, setData] = useState<UserRange[]>()
  const [pagginationState, setPagginationState] = useState<StaticPaginatedTableState>({
    pageNumber: 1,
    pageSize: 5
  })
  const [orderManager, setOrderManager] = useState<OrderManager>(() => new OrderManager(
    UserRangeOrderCols,
    UserRangeDefaultOrder,
    (order: Order) => {
      setOrderManager(current => current.newManager(order))
      setPagginationState(current => ({ ...current, pageNumber: 1 }))
    }
  ))

  function refresh() {
    services.session.fetchWithToken(`/api/communal/my/reports/usersranking?from=${wizardData.from.toISOString()}&to=${wizardData.to.toISOString()}`).then((response) => {
      return response.json()
    }).then((data: UserRange[]) => {
      data ||= []
      setData(data.sort((a, b) => b.onFireRatio - a.onFireRatio))
    })
  }
  
  function loadPDF() {
    fetchPDF({
      url: `/api/communal/my/reports/usersranking/pdf?from=${wizardData.from.toISOString()}&to=${wizardData.to.toISOString()}&lang=${session?.user.lang}`,
      fileName: "usersranking.pdf",
      services
    })
  }

  function loadExcel() {
    fetchExcel({ url: `/api/communal/my/reports/usersranking/excel?from=${wizardData.from.toISOString()}&to=${wizardData.to.toISOString()}&lang=${session?.user.lang}`, fileName: 'users-ranking.xlsx', services })
  }

  useEffect(refresh, [])

  const orderedData = useMemo(() => {
    return orderManager.orderListByKey(data || [])
  }, [data, orderManager])

  if (translates === undefined || wizardData === undefined || data === undefined) {
    return <div className='spinner'></div>
  }


  function headerRow(key: string, transKey?: string, sortable = false) {
    if (translates === undefined || wizardData === undefined) {
      return null
    }
    transKey = transKey ? transKey : key
    return <div className='hcol' onClick={sortable ? () => orderManager.click(key) : undefined}>
      {translates.for('report.usersranking.summary.table.' + transKey)}
      {sortable && orderManager.orderInfoElement(key)}
    </div>
  }

  const renderTable = (
    data: UserRange[], 
    state: StaticPaginatedTableState,
    setPagginationState: React.Dispatch<React.SetStateAction<StaticPaginatedTableState>> | undefined,
    sortable = false
  ) => {
    return <StaticPaginatedTable<UserRange>
      data={data}
      parentState={state}
      setParentState={setPagginationState}
      header={() => <>
        {headerRow('username', 'username', sortable)}
        {headerRow('totalOnFire', 'total_on_fire', sortable)}
        {headerRow('totalActive', 'active_time', sortable)}
        <div className='hcol' onClick={sortable ? () => orderManager.click('totalMth') : undefined}>
          MTH
          {sortable && orderManager.orderInfoElement('totalMth')}
        </div>
        {headerRow('onFireRatio', 'ratio', sortable)}
      </>}
      row={(row) => {
        const openDetails = () => {
          const url = `${window.location.origin}/communal/statistics/users?length=1d&user=${row.username}`;
          window.open(url, '_blank');
        }
        return <>
          <div className={`col`} onClick={openDetails}>{row.username}</div>
          <div className={`col`} onClick={openDetails}>
            <TimeDHM dhm={secondsToDHM(row.totalOnFire * tenMinInSec)} translates={translates} />
          </div>
          <div className={`col`} onClick={openDetails}>
            <TimeDHM dhm={secondsToDHM(row.totalActive * tenMinInSec)} translates={translates} />
          </div>
          <div className={`col`} onClick={openDetails}>{row.totalMth?.toFixed(2) ?? '-'}</div>
          <div className={`col`} onClick={openDetails}>{((row.onFireRatio || 0)*100).toFixed(2) + "%"}</div>
        </>
      }}
      translates={translates}
    />
  }

  return <>
    <div>
      <h2>{translates.for('report.usersranking.summary.title')}</h2>
      <TimeZoneNotice />

      <div className='right-btns right-btns-margin'>
        <button type="button" className='btn btn-next' onClick={() => {
          orderManager.setCurrentOrderKey('totalActive')
          orderManager.setDirection(false)
        }}>{translates.for('report.usersranking.summary.best')}</button>
        <button type="button" className='btn btn-next' onClick={() => {
          orderManager.setCurrentOrderKey('totalActive')
          orderManager.setDirection(true)
        }}>{translates.for('report.usersranking.summary.worst')}</button>
      </div>

      <h3>{translates.for('report.usersranking.summary.all_title')}</h3>
      {renderTable(orderedData, pagginationState, setPagginationState, true)}

      <div className='right-btns right-btns-margin'>
        <button type="button" className='btn btn-next' onClick={loadExcel}>{translates.for('popup.export.excel_export')}</button>
        <button type="button" className='btn btn-next' onClick={loadPDF}>{translates.for('report.usersranking.wizard.gen_pdf')}</button>
      </div>
    </div>
  </>
}
